import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, CheckCircle, XCircle, Clock, Check } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

const fmtAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const ICON = {
  payment_approved: <CheckCircle size={16} weight="fill" className="text-emerald-600" />,
  payment_rejected: <XCircle size={16} weight="fill" className="text-rose-600" />,
  payment_request: <Clock size={16} weight="fill" className="text-amber-600" />,
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, unread: 0 });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const limit = 10;
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / limit));

  const loadUnread = useCallback(() => {
    api.get("/notifications/unread-count").then(({ data }) => setData((d) => ({ ...d, unread: data.unread })))
      .catch(() => {});
  }, []);

  const loadList = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { page: p, limit } });
      setData(data);
    } finally { setLoading(false); }
  }, [page]);

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    return () => clearInterval(t);
  }, [loadUnread]);

  // Close on outside click / Escape — the portal panel lives outside the sidebar
  // DOM so we check against BOTH the panel and the bell button.
  useEffect(() => {
    if (!open) return;
    loadList(1); setPage(1);
    const onDown = (e) => {
      const t = e.target;
      if (panelRef.current && panelRef.current.contains(t)) return;
      if (btnRef.current && btnRef.current.contains(t)) return;
      setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, loadList]);

  const openNotif = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      setData((d) => ({ ...d, items: d.items.map((x) => x.id === n.id ? { ...x, read: true } : x), unread: Math.max(0, d.unread - 1) }));
    }
    if (n.link) { setOpen(false); navigate(n.link); }
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
    setData((d) => ({ ...d, items: d.items.map((x) => ({ ...x, read: true })), unread: 0 }));
  };

  const goPage = (p) => { setPage(p); loadList(p); };

  const panel = open ? createPortal(
    <>
      {/* Full-page scrim so the panel visibly separates from page content and
          absorbs clicks on empty areas (the mousedown listener also closes). */}
      <div
        className="fixed inset-0 bg-slate-900/5 z-[9998]"
        data-testid="notification-scrim"
        onClick={() => setOpen(false)}
      />
      <div
        ref={panelRef}
        className="fixed md:left-[272px] md:top-16 right-2 top-14 w-[380px] max-w-[calc(100vw-1rem)] bg-white border border-slate-300 rounded-md shadow-2xl z-[9999] overflow-hidden isolate"
        data-testid="notification-panel"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-900">Notifications</div>
          {data.unread > 0 && (
            <button onClick={markAll} className="text-[11px] text-blue-600 hover:text-blue-700 inline-flex items-center gap-1" data-testid="notif-read-all">
              <Check size={11} /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-xs text-slate-500">Loading…</div>
          ) : data.items.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500">No notifications yet.</div>
          ) : (
            data.items.map((n) => (
              <button
                key={n.id}
                onClick={() => openNotif(n)}
                data-testid={`notif-${n.id}`}
                className={`w-full flex items-start gap-2 px-3 py-2.5 text-left border-b border-slate-50 hover:bg-slate-50 ${n.read ? "" : "bg-blue-50/40"}`}
              >
                <div className="shrink-0 mt-0.5">{ICON[n.type] || <Clock size={16} className="text-slate-400" />}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-900 break-words">{n.title}</div>
                  <div className="text-[11px] text-slate-600 break-words">{n.body}</div>
                  {n.type === "payment_rejected" && n.meta?.note && (
                    <div className="text-[11px] italic text-rose-600 mt-0.5 break-words">“{n.meta.note}”</div>
                  )}
                  <div className="text-[10px] text-slate-400 mt-0.5">{fmtAgo(n.created_at)}</div>
                </div>
                {!n.read && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 shrink-0" />}
              </button>
            ))
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 text-[11px]">
            <button disabled={page <= 1} onClick={() => goPage(page - 1)} className="text-slate-600 disabled:opacity-40" data-testid="notif-prev">← Prev</button>
            <span className="text-slate-500">Page {page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => goPage(page + 1)} className="text-slate-600 disabled:opacity-40" data-testid="notif-next">Next →</button>
          </div>
        )}
      </div>
    </>,
    document.body,
  ) : null;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md hover:bg-slate-800/60 transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={18} weight={data.unread > 0 ? "fill" : "regular"} />
        {data.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center" data-testid="notif-unread-count">
            {data.unread > 99 ? "99+" : data.unread}
          </span>
        )}
      </button>
      {panel}
    </div>
  );
}
